/**
 * Is this copy of the app current, and if not, where does the worker go?
 *
 * Checked on launch and on every resume, because a phone that lives in a pocket never "launches"
 * again — but cached, because a worker who picks their phone up twenty times a shift should not
 * cost twenty requests. The last good answer is kept and shown while a new one is fetched, so a
 * moment without signal never blanks the screen or invents a problem.
 *
 * Where to send them: the Play listing, always. Until 26 October 2026 a copy that did not report
 * Play as its installer was sent to our own download page instead, and Google suspended the app
 * for it (Device and Network Abuse: "apps that cause users to download or install applications
 * from unknown sources outside of Google Play"). A Play app never points at an APK, whoever
 * installed it.
 */
import { api } from './api.js';
import { appInfo, isNativeApp } from './native.js';

export type LatestRelease = {
  version_code: number;
  version_name: string;
  url: string;
  sha256: string;
  mandatory: boolean;
  notes: string | null;
  published_at: string;
};

export type VersionInfo = {
  latest: LatestRelease | null;
  minimum_version_code: number;
  /** Where a sideloaded copy goes to update: our own page, which carries the instructions. */
  download_url?: string;
  /** Printed on the blocking screen: somebody stuck must have a person to call. */
  help_phone?: string;
  /**
   * How to get the app from Play, when the owner has set that route up. Null until then, and
   * the direct download is the way in.
   */
  play?: {
    route: 'group' | 'public';
    group_url: string | null;
    opt_in_url: string;
    store_url: string;
  } | null;
};

export type Standing =
  /** Not the app at all (a browser, an iPhone), or nothing published yet. Nothing to say. */
  | 'not_applicable'
  | 'current'
  | 'update_available'
  /** Below the minimum: the server will refuse a punch from this build. */
  | 'too_old'
  /** We could not find out. Never used to block anybody — see the comment on `decide`. */
  | 'unknown';

export type VersionState = {
  standing: Standing;
  mine: number | null;
  info: VersionInfo | null;
  /** True when the last attempt to ask the server failed. */
  stale: boolean;
  /** Play, or our download page, depending on where this copy came from. */
  updateUrl: string | null;
  fromPlay: boolean;
};

/** How long an answer is good for. A resume inside this window costs nothing. */
const TTL_MS = 15 * 60 * 1000;

/**
 * The decision, as a pure function of what we know.
 *
 * `unknown` is deliberately not a refusal. A version check that cannot reach the server must fail
 * open: the alternative is an app that bricks itself the moment the office wifi drops, and the
 * server refuses an out-of-date punch anyway, which is the enforcement that matters.
 */
export function decide(input: { mine: number | null; info: VersionInfo | null }): Standing {
  if (input.mine === null) return 'not_applicable';
  if (!input.info) return 'unknown';
  if (input.mine < input.info.minimum_version_code) return 'too_old';
  if (input.info.latest && input.mine < input.info.latest.version_code) return 'update_available';
  return 'current';
}

/** The app's own package, for when the phone does not say. */
export const APP_PACKAGE = 'in.co.sivalogistics.attendance';

/** The Play listing. The only place the app ever sends anybody to update (see the top). */
export const playListing = (packageName?: string | null) =>
  `https://play.google.com/store/apps/details?id=${packageName || APP_PACKAGE}`;

export function updateUrlFor(input: { packageName: string | null | undefined }): {
  url: string;
  fromPlay: true;
} {
  return { url: playListing(input.packageName), fromPlay: true };
}

let cached: { at: number; state: VersionState } | null = null;

const EMPTY: VersionState = {
  standing: 'not_applicable',
  mine: null,
  info: null,
  stale: false,
  updateUrl: null,
  fromPlay: false,
};

/**
 * Throw the cached answer away. For tests only — the app uses `force` instead, which refetches
 * without discarding what it already knew, so a failed retry can still fall back to it.
 */
export function forgetVersion(): void {
  cached = null;
}

/**
 * The current standing, from the cache when it is fresh enough.
 *
 * Returns the previous answer with `stale: true` when the server cannot be reached, rather than
 * nothing: on the blocking screen the difference between "you are out of date" and "we could not
 * check just now" is the difference between a worker who knows what to do and one who is stuck.
 */
export async function versionState(opts: { force?: boolean } = {}): Promise<VersionState> {
  if (!isNativeApp()) return EMPTY;
  const now = Date.now();
  if (!opts.force && cached && now - cached.at < TTL_MS) return cached.state;

  const mine = await appInfo();
  if (!mine) return EMPTY;
  try {
    const info = await api<VersionInfo>('/android/version');
    const { url, fromPlay } = updateUrlFor({ packageName: mine.package });
    const state: VersionState = {
      standing: decide({ mine: mine.version_code, info }),
      mine: mine.version_code,
      info,
      stale: false,
      updateUrl: url,
      fromPlay,
    };
    cached = { at: now, state };
    return state;
  } catch {
    const previous = cached?.state;
    const state: VersionState = {
      standing: previous?.standing ?? 'unknown',
      mine: mine.version_code,
      info: previous?.info ?? null,
      stale: true,
      // Play needs nothing from the server, so a failed check still has somewhere to send them.
      updateUrl: updateUrlFor({ packageName: mine.package }).url,
      fromPlay: true,
    };
    // Not cached: a failure must not become the answer for the next fifteen minutes.
    return state;
  }
}

/**
 * Run `fn` on launch and whenever the app comes back to the foreground.
 *
 * Returns the unsubscribe. The resume check is the important one: a worker opens the app, punches
 * in, and puts the phone away for eight hours — the next thing that happens is a resume, not a
 * launch, and a check that only ran at launch would never run again all day.
 */
export function onLaunchAndResume(fn: () => void): () => void {
  fn();
  const onVisible = () => {
    if (document.visibilityState === 'visible') fn();
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => document.removeEventListener('visibilitychange', onVisible);
}
